import React, { RefObject } from 'react'
import { styled } from '@mui/material/styles'
import { Avatar, IconButton, Typography, Tooltip } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import { BsEmojiSmile, BsX } from 'react-icons/bs'

export interface ChatMessage {
  author: string
  content: string
  createdAt: number
}

interface ChatSidebarProps {
  messages: ChatMessage[]
  inputValue: string
  disabled?: boolean
  showEmojiPicker: boolean
  onChangeInput: (val: string) => void
  onSubmit: (e: React.FormEvent) => void
  onToggleEmojiPicker: () => void
  onEmojiSelect: (emoji: string) => void
  messagesRef: RefObject<HTMLDivElement>
  inputRef: RefObject<HTMLInputElement>
  emojiButtonRef: RefObject<HTMLButtonElement>
  onInputFocus?: () => void
  onInputBlur?: () => void
  onClose?: () => void
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({
  messages,
  inputValue,
  disabled,
  showEmojiPicker,
  onChangeInput,
  onSubmit,
  onToggleEmojiPicker,
  onEmojiSelect,
  messagesRef,
  inputRef,
  emojiButtonRef,
  onInputFocus,
  onInputBlur,
  onClose,
}) => {
  const dateFormatter = React.useMemo(() => new Intl.DateTimeFormat('en', { timeStyle: 'short' }), [])

  return (
    <SidebarWrapper>
      <ChatHeader>
        <ChatTitle variant="subtitle1"># Local Room</ChatTitle>
        {onClose && (
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close chat"
            sx={{
              color: 'var(--sevv-text-secondary, #757575)',
              padding: '4px',
              '&:hover': {
                backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 15%, transparent)',
                color: 'var(--sevv-primary, #f97415)',
              },
            }}
          >
            {React.createElement(BsX as any, { size: 18 })}
          </IconButton>
        )}
      </ChatHeader>
      <ChatMessages ref={messagesRef}>
        {messages.map((msg, idx) => (
          <MessageRow key={idx}>
            <Tooltip title={msg.author || 'Unknown'} arrow placement="right">
              <Avatar 
                sx={{ 
                  width: 24, 
                  height: 24, 
                  fontSize: 12,
                  backgroundColor: 'var(--sevv-primary, #f97415)',
                  color: '#ffffff',
                }}
              >
                {msg.author ? msg.author[0] : '?'}
              </Avatar>
            </Tooltip>
            <div>
              <Typography variant="caption" sx={{ color: 'var(--sevv-text-secondary, #757575)', fontWeight: 600, fontSize: '11px' }}>
                {msg.author || 'Unknown'}
              </Typography>
              <MessageContent>{msg.content}</MessageContent>
              <Typography variant="caption" sx={{ color: 'var(--sevv-gray-400, #bdbdbd)', fontSize: '10px' }}>
                {msg.createdAt ? dateFormatter.format(new Date(msg.createdAt)) : ''}
              </Typography>
            </div>
          </MessageRow>
        ))}
      </ChatMessages>
      {/* Input + Emoji Picker */}
      <div style={{ position: 'relative' }}>
        <EmojiPickerContainer show={showEmojiPicker} data-emoji-picker>
          <EmojiSection>
            <EmojiCategory>Smileys & People</EmojiCategory>
            <EmojiGrid>
              {['😀','😃','😄','😁','😆','😅','😂','🤣','😊','😇','🙂','🙃','😉','😌','😍','🥰','😘','😗','😙','😚','😋','😛','😝','😜','🤪','🤨','🧐','🤓','😎','🤩','🥳','😏','😒','😞','😔','😟','😕','🙁','☹️','😣','😖','😫','😩','🥺','😢','😭','😤','😠','😡','🤬','🤯','😳','🥵','🥶','😱','😨','😰','😥','😓','🤗','🤔','🤭','🤫','🤥','😶','😐','😑','😬','🙄','😯','😦','😧','😮','😲','🥱','😴','🤤','😪','😵','🤐','🥴','🤢','🤮','🤧','😷','🤒','🤕','🤑','🤠','😈','👿','👹','👺','🤡','💩','👻'].map((emoji) => (
                <EmojiButton key={emoji} onClick={() => onEmojiSelect(emoji)} title={emoji}>
                  {emoji}
                </EmojiButton>
              ))}
            </EmojiGrid>
          </EmojiSection>
          
          <EmojiSection>
            <EmojiCategory>Gestures & Body</EmojiCategory>
            <EmojiGrid>
              {['👋','🤚','🖐️','✋','🖖','👌','🤌','🤏','✌️','🤞','🤟','🤘','🤙','👈','👉','👆','🖕','👇','☝️','👍','👎','👊','✊','🤛','🤜','👏','🙌','👐','🤲','🤝','🙏','✍️'].map((emoji) => (
                <EmojiButton key={emoji} onClick={() => onEmojiSelect(emoji)} title={emoji}>
                  {emoji}
                </EmojiButton>
              ))}
            </EmojiGrid>
          </EmojiSection>
          
          <EmojiSection>
            <EmojiCategory>Objects & Symbols</EmojiCategory>
            <EmojiGrid>
              {['❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖','💘','💝','💟','☮️','✝️','☪️','🕉️','☸️','✡️','🔯','🕎','☯️','☦️','🛐','⛎','♈','♉','♊','♋','♌','♍','♎','♏','♐','♑','♒','♓','🆔','⚛️','🉑','☢️','☣️'].map((emoji) => (
                <EmojiButton key={emoji} onClick={() => onEmojiSelect(emoji)} title={emoji}>
                  {emoji}
                </EmojiButton>
              ))}
            </EmojiGrid>
          </EmojiSection>
          
          <EmojiSection>
            <EmojiCategory>Activities & Food</EmojiCategory>
            <EmojiGrid>
              {['⚽','🏀','🏈','⚾','🥎','🎾','🏐','🏉','🥏','🎱','🪀','🏓','🏸','🏒','🏑','🥍','🏏','🪃','🥅','⛳','🪁','🏹','🎣','🤿','🥊','🥋','🎽','🛹','🛷','⛸️','🥌','🎿','⛷️','🏂','🏃','🏄','🏊','🤽','🚣','🧗','🚵','🚴','🏆','🥇','🥈','🥉','🏅','🎖️','🏵️','🎗️','🎫','🎟️','🎪','🤹','🎭','🎨'].map((emoji) => (
                <EmojiButton key={emoji} onClick={() => onEmojiSelect(emoji)} title={emoji}>
                  {emoji}
                </EmojiButton>
              ))}
            </EmojiGrid>
          </EmojiSection>
        </EmojiPickerContainer>
        <ChatInputContainer onSubmit={onSubmit}>
          <ChatInput
            type="text"
            placeholder="Message # Local Room"
            value={inputValue}
            onChange={(e) => onChangeInput(e.target.value)}
            autoComplete="off"
            ref={inputRef}
            disabled={disabled}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
          />
          <IconButton
            ref={emojiButtonRef}
            onClick={onToggleEmojiPicker}
            size="small"
            sx={{
              color: showEmojiPicker ? 'var(--sevv-primary, #f97415)' : 'var(--sevv-text-secondary, #757575)',
              padding: '4px',
              transition: 'all 0.2s',
              '&:hover': {
                color: 'var(--sevv-primary, #f97415)',
                backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent)',
              },
            }}
            aria-label="Toggle emoji picker"
          >
            {React.createElement(BsEmojiSmile as any, { size: 16 })}
          </IconButton>
          <IconButton 
            type="submit" 
            size="small" 
            sx={{ 
              color: 'var(--sevv-primary, #f97415)', 
              padding: '4px',
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent)',
              },
              '&:disabled': {
                color: 'var(--sevv-gray-400, #bdbdbd)',
              },
            }} 
            disabled={disabled}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </ChatInputContainer>
      </div>
    </SidebarWrapper>
  )
}

export default ChatSidebar

/* Styled Components */
const SidebarWrapper = styled('div')({
  width: '300px',
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  display: 'flex',
  flexDirection: 'column',
  borderRight: '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 20%, transparent)',
  backdropFilter: 'blur(10px)',
  boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.1)',
})

const ChatHeader = styled('div')({
  padding: '12px 16px',
  borderBottom: '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 20%, transparent)',
  backgroundColor: 'var(--sevv-bg-default, #fafafa)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
})

const ChatTitle = styled(Typography)({
  color: 'var(--sevv-text-primary, #424242)',
  fontWeight: 700,
  fontSize: '16px',
})

const ChatMessages = styled('div')({
  flex: 1,
  padding: '12px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  backgroundColor: 'var(--sevv-bg-paper, #ffffff)',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
})

const MessageRow = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
})

const MessageContent = styled('div')({
  backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent)',
  color: 'var(--sevv-text-primary, #424242)',
  borderRadius: '6px',
  padding: '6px 10px',
  fontSize: '13px',
  maxWidth: '200px',
  wordBreak: 'break-word',
  border: '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)',
})

const ChatInputContainer = styled('form')({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 12px',
  borderTop: '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 20%, transparent)',
  backgroundColor: 'var(--sevv-bg-default, #fafafa)',
  gap: '8px',
})

const ChatInput = styled('input')({
  flex: 1,
  backgroundColor: 'var(--sevv-bg-paper, #ffffff)',
  border: '1px solid var(--sevv-divider, #eeeeee)',
  color: 'var(--sevv-text-primary, #424242)',
  fontSize: '14px',
  borderRadius: '4px',
  padding: '6px 10px',
  outline: 'none',
  transition: 'border-color 0.2s ease',
  '&:focus': {
    borderColor: 'var(--sevv-primary, #f97415)',
  },
  '&::placeholder': { color: 'var(--sevv-gray-400, #bdbdbd)' },
})

const EmojiPickerContainer = styled('div')<{ show: boolean }>(({ show }) => ({
  position: 'absolute',
  bottom: '64px',
  left: '12px',
  right: '12px',
  backgroundColor: 'var(--sevv-bg-paper, #ffffff)',
  border: '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)',
  borderRadius: '12px',
  padding: '16px',
  display: show ? 'block' : 'none',
  zIndex: 20,
  maxHeight: '240px',
  overflowY: 'auto',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
  backdropFilter: 'blur(12px)',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
}))

const EmojiSection = styled('div')({
  marginBottom: '20px',
  '&:last-child': {
    marginBottom: '0',
  },
})

const EmojiCategory = styled('div')({
  color: 'var(--sevv-primary, #f97415)',
  fontSize: '11px',
  fontWeight: '700',
  margin: '0 0 12px 4px',
  textAlign: 'left',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  opacity: 0.9,
  borderBottom: '1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 20%, transparent)',
  paddingBottom: '6px',
})

const EmojiGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(8, 1fr)',
  gap: '6px',
  alignItems: 'center',
  justifyItems: 'center',
  padding: '8px 4px',
})

const EmojiButton = styled('button')({
  background: 'none',
  border: 'none',
  fontSize: '18px',
  cursor: 'pointer',
  padding: '8px',
  borderRadius: '6px',
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  '&:hover': {
    backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 15%, transparent)',
    transform: 'scale(1.1)',
    boxShadow: '0 2px 8px color-mix(in srgb, var(--sevv-primary, #f97415) 20%, transparent)',
  },
  '&:active': {
    backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 25%, transparent)',
    transform: 'scale(1.05)',
  },
}) 